/**
 * @fileoverview Hook for subscribing to legacy migration run history from Dexie.
 * @module system-web/hooks/useLegacyRunHistory
 *
 * Responsibilities:
 * - Subscribe to migration runs via Dexie liveQuery (local-first, offline-capable).
 * - Expose run history newest-first for the RunHistoryPanel.
 *
 * Dependencies:
 * - dexie (liveQuery)
 * - @firesquirrel/local-db
 * - ../services/legacyMigrationService
 *
 * Architecture:
 * - Runs are local-first: liveQuery on `legacy_migration_runs`.
 * - liveQuery observed function performs ONLY reads — no writes, no API calls (§liveQuery contract).
 *
 * Notes:
 * - startRun() in legacyMigrationService inserts a provisional run row, which liveQuery
 *   immediately surfaces. The authoritative row arrives via system sync pull.
 * - When any run is in 'pending' or 'running' status, a polling effect fetches updated
 *   runs from the server every 3 seconds and writes them to Dexie for instant UI updates.
 * - refresh() is exposed for manual "Refresh Runs" / "Refresh History" buttons. It does not
 *   rely solely on the liveQuery subscription picking up the sync write — it explicitly
 *   re-reads Dexie and updates state afterward, so a manual click is guaranteed to reflect
 *   the latest server state even if the background replication engine's write to Dexie
 *   (a separate write path from this hook's own sync helpers) doesn't retrigger this
 *   specific liveQuery observable right away.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { liveQuery } from 'dexie';
import { getSystemDb } from '@firesquirrel/local-db';
import { listRuns, fetchAndSyncRuns, fetchAndSyncReceipts, type MigrationRunRow } from '../services/legacyMigrationService';

/**
 * @contract LegacyRunHistoryState
 *
 * Purpose:
 * - Shape returned by useLegacyRunHistory.
 *
 * Fields:
 * - runs: Migration run rows from Dexie, newest first.
 * - isLoading: True while the initial Dexie query is resolving.
 * - error: Non-null when the Dexie subscription fails.
 * - refresh: Manually re-syncs runs/receipts from the server and re-reads Dexie into state.
 *
 * Rules:
 * - runs is [] before first sync or when tenantId/datasetKey is unset.
 *
 * Sync:
 * - Dexie mirrors system Mongo `legacyMigrationRuns` via system sync pull.
 */
export interface LegacyRunHistoryState {
  runs: MigrationRunRow[];
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * @hook useLegacyRunHistory
 *
 * Purpose:
 * - Provide reactive run history for the RunHistoryPanel component.
 *
 * Returns:
 * - runs (newest first), isLoading, error
 *
 * Side Effects:
 * - Subscribes a Dexie liveQuery for runs (torn down on unmount / dep change).
 *
 * Offline Behavior:
 * - Fully offline-capable — reads from Dexie.
 */
export function useLegacyRunHistory(
  tenantId: string | null,
  datasetKey: string | null,
  legacyDeptId: number | null,
): LegacyRunHistoryState {
  const [runs, setRuns] = useState<MigrationRunRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  // Kept current via effect below so refresh() (a stable callback) always reads the
  // latest tenant/dataset/department without needing to be recreated on every change.
  const paramsRef = useRef({ tenantId, datasetKey, legacyDeptId });
  useEffect(() => {
    paramsRef.current = { tenantId, datasetKey, legacyDeptId };
  }, [tenantId, datasetKey, legacyDeptId]);

  useEffect(() => {
    if (!tenantId || !datasetKey) {
      setRuns([]);
      setIsLoading(false);
      return;
    }

    let subscription: { unsubscribe: () => void } | null = null;
    let cancelled = false;
    setIsLoading(true);

    getSystemDb()
      .then((db) => {
        if (cancelled) return;
        subscription = liveQuery(
          () => listRuns(tenantId, datasetKey),
        ).subscribe({
          next: (rows) => {
            if (!cancelled && !unmountedRef.current) {
              setRuns(rows);
              setIsLoading(false);
            }
          },
          error: (err: unknown) => {
            if (!cancelled && !unmountedRef.current) {
              const msg = err instanceof Error ? err.message : 'Failed to load run history';
              console.warn('[useLegacyRunHistory] liveQuery error:', err);
              setError(msg);
              setIsLoading(false);
            }
          },
        });
        void db;
      })
      .catch((err) => {
        if (!cancelled && !unmountedRef.current) {
          const msg = err instanceof Error ? err.message : 'Failed to open Dexie';
          console.warn('[useLegacyRunHistory] getSystemDb error:', err);
          setError(msg);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, [tenantId, datasetKey]);

  // Poll for updated runs every 3 seconds while on this dataset.
  // This ensures background worker runs (running / in_progress / completed)
  // are immediately fetched from the server and rendered live in the UI.
  useEffect(() => {
    if (!legacyDeptId || !datasetKey) return;

    let cancelled = false;
    const POLL_INTERVAL_MS = 3000;

    const poll = async () => {
      if (cancelled || unmountedRef.current) return;
      await fetchAndSyncRuns(legacyDeptId, datasetKey);
      if (datasetKey !== 'department-wide') {
        await fetchAndSyncRuns(legacyDeptId, 'department-wide');
      }
      await fetchAndSyncReceipts(legacyDeptId, datasetKey);
      const { tenantId: currentTenantId, datasetKey: currentDatasetKey } = paramsRef.current;
      if (!currentTenantId || !currentDatasetKey) return;
      const rows = await listRuns(currentTenantId, currentDatasetKey);
      if (!cancelled && !unmountedRef.current) setRuns(rows);
    };

    // Always poll immediately on mount/dataset change
    void poll();

    const interval = setInterval(() => { void poll(); }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [legacyDeptId, datasetKey]);

  // Manual refresh: re-sync from the server, then explicitly re-read Dexie and update
  // state directly. Does not rely on the liveQuery subscription alone re-emitting, so a
  // manual click is always reflected immediately regardless of background replication timing.
  const refresh = useCallback(async () => {
    const { tenantId: currentTenantId, datasetKey: currentDatasetKey, legacyDeptId: currentLegacyDeptId } = paramsRef.current;
    if (!currentTenantId || !currentDatasetKey) return;

    setIsRefreshing(true);
    try {
      if (currentLegacyDeptId) {
        await fetchAndSyncRuns(currentLegacyDeptId, currentDatasetKey);
        if (currentDatasetKey !== 'department-wide') {
          await fetchAndSyncRuns(currentLegacyDeptId, 'department-wide');
        }
        await fetchAndSyncReceipts(currentLegacyDeptId, currentDatasetKey);
      }

      const rows = await listRuns(currentTenantId, currentDatasetKey);
      if (!unmountedRef.current) setRuns(rows);
    } catch (err) {
      if (!unmountedRef.current) {
        const msg = err instanceof Error ? err.message : 'Failed to refresh run history';
        console.warn('[useLegacyRunHistory] refresh error:', err);
        setError(msg);
      }
    } finally {
      if (!unmountedRef.current) {
        setIsRefreshing(false);
      }
    }
  }, []);

  return { runs, isLoading, isRefreshing, error, refresh };
}
